import { type FC, type ReactElement, useState } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import { ButtonLinkLegacy } from '@/components/ui/button-link-legacy'
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger
} from '@/components/ui/dialog'
import { Spinner } from '@/components/ui/spinner'
import { useSaveVisualVettingDraft } from '@/features/luxe-submissions/queries/use-save-visual-vetting-draft'
import type { ScoreFormData } from '@/features/luxe-submissions/types'
import { routes } from '@/routes'
import { useTranslation } from '@/translations/i18n'

interface SaveDraftDialogProps {
	listingId: string
	trigger: ReactElement
	getFormData: () => ScoreFormData
}

export const SaveDraftDialog: FC<SaveDraftDialogProps> = ({
	listingId,
	trigger,
	getFormData
}) => {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const location = useLocation()
	const fromSearch = location.state?.fromSearch
	const [open, setOpen] = useState(false)
	const { mutate, isPending } = useSaveVisualVettingDraft(listingId, {
		onSuccess: () => {
			setOpen(false)
			navigate({
				pathname:
					routes.LUXE_SUBMISSIONS_QUEUE_VISUAL_VETTING.handle
						.fullPath,
				search: fromSearch
			})
		}
	})

	return (
		<Dialog onOpenChange={setOpen} open={open}>
			<DialogTrigger render={trigger} />
			<DialogContent className='max-w-120' showCloseButton={false}>
				<DialogHeader>{t('luxe_submissions:save_draft')}</DialogHeader>

				<DialogBody className='gap-2'>
					<DialogTitle>
						{t('luxe_submissions:save_draft_question')}
					</DialogTitle>
					<DialogDescription>
						{t('luxe_submissions:save_draft_description')}
					</DialogDescription>
				</DialogBody>

				<DialogFooter className='flex flex-row justify-between bg-neutral-white'>
					<ButtonLinkLegacy
						to={{
							pathname:
								routes.LUXE_SUBMISSIONS_QUEUE_VISUAL_VETTING
									.handle.fullPath,
							search: fromSearch
						}}
						type='button'
						variant='tertiary'
					>
						{t('common:close')}
					</ButtonLinkLegacy>
					<ButtonLegacy
						disabled={isPending}
						onClick={() => {
							const formData = getFormData()
							mutate({
								answers: Object.entries(formData.answers).map(
									([questionId, answer]) => ({
										questionId,
										answer: answer || null
									})
								)
							})
						}}
						type='button'
						variant='brand'
					>
						{t('luxe_submissions:save_draft_confirm')}
						{isPending && <Spinner />}
					</ButtonLegacy>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}
